import type { StudentPackageDTO } from "@/modules/students/domain/repo/list-packages";
import type { PrivateLessonPackageHistoryDTO } from "@/modules/students/domain/repo/list-private-lesson-packages";
import { formatDate } from "@/shared/lib/date-time";
import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";

export type PackageRow =
  | (StudentPackageDTO & { packageType: "package" })
  | (PrivateLessonPackageHistoryDTO & {
      packageType: "private_lesson_package";
    });

type PackageHistoryTableProps = {
  data: PackageRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, _limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onViewDetails?: (pkg: PackageRow) => void;
};

export function PackageHistoryTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewDetails,
}: PackageHistoryTableProps) {
  const columns: TanstackReactTable.ColumnDef<PackageRow>[] = [
    {
      header: "ປະເພດ",
      accessorKey: "packageType",
      cell: ({ row }) => {
        const type = row.original.packageType;
        return (
          <Badge variant="outline">
            {type === "package" ? "ແພັກເກດ (ຄັ້ງ)" : "ແພັກເກດຮຽນສ່ວນຕົວ (ຊົ່ວໂມງ)"}
          </Badge>
        );
      },
    },
    createSortableColumn("templateName", "ຊື່ແພັກເກດ"),
    {
      header: "ຈຳນວນທັງໝົດ",
      accessorKey: "total",
      cell: ({ row }) => {
        if (row.original.packageType === "package") {
          return `${row.original.totalSessions} ຄັ້ງ`;
        }
        return `${row.original.totalHours} ຊົ່ວໂມງ`;
      },
    },
    {
      header: "ຈຳນວນທີເຫຼືອ",
      accessorKey: "remaining",
      cell: ({ row }) => {
        if (row.original.packageType === "package") {
          return `${row.original.remainingSessions} ຄັ້ງ`;
        }
        return `${row.original.remainingHours} ຊົ່ວໂມງ`;
      },
    },
    {
      header: "ວັນທີເລີ່ມຕົ້ນ",
      accessorKey: "startDate",
      cell: ({ row }) => formatDate(row.original.startDate),
    },
    {
      header: "ວັນທີໝົດອາຍຸ",
      accessorKey: "expireDate",
      cell: ({ row }) => formatDate(row.original.expireDate),
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        const variant =
          status === "active"
            ? "default"
            : status === "expired"
              ? "secondary"
              : status === "used_up"
                ? "outline"
                : status === "canceled"
                  ? "destructive"
                  : "secondary";
        const label =
          status === "active"
            ? "ໃຊ້ງານ"
            : status === "expired"
              ? "ໝົດອາຍຸ"
              : status === "used_up"
                ? "ໃຊ້ຄົບ"
                : status === "pending_cancel"
                  ? "ລໍຖ້າຍົກເລີກ"
                  : "ຍົກເລີກ";
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
    {
      header: "ວັນທີຊື້",
      accessorKey: "purchasedAt",
      cell: ({ row }) => formatDate(row.original.purchasedAt),
    },
    {
      header: "ຄລາດສ໌ທີ່ເລືອກ",
      accessorKey: "selectedClasses",
      cell: ({ row }) => {
        if (row.original.packageType !== "package") return "-";
        const pkg = row.original as StudentPackageDTO;
        const classes = pkg.selectedClasses;
        if (!classes || classes.length === 0) return "-";
        return (
          <span className="line-clamp-2 text-sm">
            {classes
              .map((c) => `${c.classTypeName} - ${c.levelName}`)
              .join(", ")}
          </span>
        );
      },
    },
  ];

  return (
    <DataTable<PackageRow, unknown>
      noDataMessage="ບໍ່ພົບແພັກເກດ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
